import { ref, type Ref } from 'vue'
import { nanoid } from 'nanoid'

export interface AddressSuggestion {
  mapboxId: string
  label: string
  description: string
}

export interface StructuredAddress {
  street1: string
  street2: string
  city: string
  state: string
  zip: string
  county: string
  country: string
}

const MAPBOX_BASE = 'https://api.mapbox.com/search/searchbox/v1'
const MIN_CHARS = 3
const DEBOUNCE_MS = 200

function getAccessToken(): string | undefined {
  return import.meta.env.VITE_MAPBOX_ACCESS_TOKEN
}

function newSessionToken(): string {
  return nanoid()
}

function mapToStructuredAddress(feature: any): StructuredAddress {
  const ctx = feature.properties?.context || {}
  const addressNumber = ctx.address?.address_number || ''
  const streetName = ctx.address?.street_name || ''
  const street1 = [addressNumber, streetName].filter(Boolean).join(' ')

  return {
    street1,
    street2: '',
    city: ctx.place?.name || ctx.locality?.name || '',
    state: ctx.region?.region_code || '',
    zip: ctx.postcode?.name || '',
    county: ctx.district?.name || '',
    country: ctx.country?.country_code || '',
  }
}

export function useAddressAutocomplete() {
  const accessToken = getAccessToken()
  const enabled = Boolean(accessToken)

  const suggestions: Ref<AddressSuggestion[]> = ref([])
  const loading = ref(false)
  let sessionToken = newSessionToken()
  let debounceTimer: ReturnType<typeof setTimeout> | null = null
  let abortController: AbortController | null = null

  async function suggest(query: string, countryCode?: string) {
    if (!enabled) return

    if (debounceTimer) clearTimeout(debounceTimer)
    if (abortController) abortController.abort()

    if (!query || query.length < MIN_CHARS) {
      suggestions.value = []
      return
    }

    debounceTimer = setTimeout(async () => {
      loading.value = true
      abortController = new AbortController()

      const params = new URLSearchParams({
        q: query,
        access_token: accessToken!,
        session_token: sessionToken,
        types: 'address',
        limit: '5',
      })

      if (countryCode) {
        params.set('country', countryCode)
      }

      try {
        const res = await fetch(`${MAPBOX_BASE}/suggest?${params}`, {
          signal: abortController.signal,
        })

        if (!res.ok) {
          suggestions.value = []
          return
        }

        const data = await res.json()
        suggestions.value = (data.suggestions || []).map((s: any) => ({
          mapboxId: s.mapbox_id,
          label: s.name || s.full_address || '',
          description: s.place_formatted || '',
        }))
      } catch (e: any) {
        if (e.name !== 'AbortError') {
          suggestions.value = []
        }
      } finally {
        loading.value = false
      }
    }, DEBOUNCE_MS)
  }

  async function retrieve(mapboxId: string): Promise<StructuredAddress | null> {
    if (!enabled) return null

    loading.value = true

    const params = new URLSearchParams({
      access_token: accessToken!,
      session_token: sessionToken,
    })

    try {
      const res = await fetch(`${MAPBOX_BASE}/retrieve/${mapboxId}?${params}`)

      if (!res.ok) return null

      const data = await res.json()
      const feature = data.features?.[0]
      if (!feature) return null

      // Reset session after a successful retrieve (Mapbox billing: session ends after retrieve)
      sessionToken = newSessionToken()

      return mapToStructuredAddress(feature)
    } catch {
      return null
    } finally {
      loading.value = false
    }
  }

  function clear() {
    suggestions.value = []
    if (debounceTimer) clearTimeout(debounceTimer)
    if (abortController) abortController.abort()
  }

  return {
    enabled,
    suggestions,
    loading,
    suggest,
    retrieve,
    clear,
  }
}
